import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../api/client';
import * as XLSX from 'xlsx';
import { Upload, Download, Plus, Search, X, GitMerge, ChevronDown, ChevronRight, Edit2, Trash2, CheckSquare, Square, CheckCircle, Clock, Users, ArrowUpDown, ArrowUp, ArrowDown, Columns3, Check, Calendar, XCircle, Pencil, Building2, AlertTriangle, ShieldAlert } from 'lucide-react';
import VendorApprovalModal from '../components/VendorApprovalModal';
import DeleteVendorModal from '../components/DeleteVendorModal';
import { usePermissions } from '../context/PermissionsContext';
import TablePagination from '../components/TablePagination';

const VENDORS_PAGE_SIZE = 25;

// Compute vendor risk flags based on insurance/contract expiry dates
function getVendorRisks(vendor) {
  const risks = [];
  const today = new Date();
  const in60Days = new Date(today); in60Days.setDate(today.getDate() + 60);
  const in30Days = new Date(today); in30Days.setDate(today.getDate() + 30);

  if (vendor.HasInsurance && vendor.InsuranceExpiryDate) {
    const exp = new Date(vendor.InsuranceExpiryDate);
    if (exp < today) {
      risks.push({ type: 'error', label: 'Insurance Expired', title: `Insurance expired ${exp.toLocaleDateString('en-GB')}` });
    } else if (exp <= in30Days) {
      risks.push({ type: 'warning', label: 'Insurance Expiring', title: `Insurance expires ${exp.toLocaleDateString('en-GB')}` });
    }
  }

  if (vendor.HasContract && vendor.ContractEndDate) {
    const end = new Date(vendor.ContractEndDate);
    if (end < today) {
      risks.push({ type: 'error', label: 'Contract Expired', title: `Contract ended ${end.toLocaleDateString('en-GB')}` });
    } else if (end <= in60Days) {
      risks.push({ type: 'warning', label: 'Contract Ending', title: `Contract ends ${end.toLocaleDateString('en-GB')}` });
    }
  }

  if (vendor.HasContract && vendor.RenewalDate) {
    const renewal = new Date(vendor.RenewalDate);
    if (renewal >= today && renewal <= in30Days) {
      risks.push({ type: 'info', label: 'Renewal Due', title: `Renewal due ${renewal.toLocaleDateString('en-GB')}` });
    }
  }

  if (vendor.ApprovalStatus === 'Approved' && vendor.HasInsurance === false) {
    risks.push({ type: 'warning', label: 'No Insurance', title: 'Vendor has no insurance recorded' });
  }

  return risks;
}

const VENDOR_COL_KEY = 'spocodoc_vendors_columns';
const VENDOR_COLUMNS = [
  { key: 'vendorName',    label: 'Vendor Name',    default: true,  locked: true },
  { key: 'contact',       label: 'Contact',        default: true  },
  { key: 'taxId',         label: 'Tax ID',         default: true  },
  { key: 'paymentTerms',  label: 'Payment Terms',  default: false },
  { key: 'invoices',      label: 'Invoices',       default: true  },
  { key: 'gls',           label: 'GLs',            default: true  },
  { key: 'approval',      label: 'Approval',       default: true  },
  { key: 'created',       label: 'Created',        default: false },
  { key: 'approved',      label: 'Approved',       default: true  },
  { key: 'status',        label: 'Status',         default: true  },
];
const VENDOR_COL_DEFAULTS = VENDOR_COLUMNS.filter(c => c.default).map(c => c.key);
function loadVendorColPrefs() {
  try {
    const saved = JSON.parse(localStorage.getItem(VENDOR_COL_KEY) || 'null');
    if (!Array.isArray(saved)) return VENDOR_COL_DEFAULTS;
    const locked = VENDOR_COLUMNS.filter(c => c.locked).map(c => c.key);
    return [...new Set([...locked, ...saved.filter(k => VENDOR_COLUMNS.some(c => c.key === k))])];
  } catch { return VENDOR_COL_DEFAULTS; }
}

export default function VendorsList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasAnyPermission } = usePermissions();
  const [vendors, setVendors] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState('all');
  const [approvalFilter, setApprovalFilter] = useState(searchParams.get('approval') || '');
  const [glFilter, setGlFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');

  // Sync search param from URL (e.g. from global search navigation)
  useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    if (urlSearch) setSearchTerm(urlSearch);
  }, [searchParams]);

  // Sync approval filter from URL (notification task links)
  useEffect(() => {
    setApprovalFilter(searchParams.get('approval') || '');
  }, [searchParams]);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [selectedKeepVendor, setSelectedKeepVendor] = useState(null);
  const [selectedRemoveVendor, setSelectedRemoveVendor] = useState(null);
  const [expandedVendor, setExpandedVendor] = useState(null);
  const [vendorGLs, setVendorGLs] = useState({});
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedVendorForApproval, setSelectedVendorForApproval] = useState(null);
  const [vendorDepartments, setVendorDepartments] = useState({});
  const [showDepartmentModal, setShowDepartmentModal] = useState(false);
  const [selectedVendorForDepts, setSelectedVendorForDepts] = useState(null);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState([]);
  const [deleteModalVendor, setDeleteModalVendor] = useState(null);
  const [sortField, setSortField] = useState('VendorName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [vendorsPage, setVendorsPage] = useState(1);

  // Period / financial-year filter
  const [periodType, setPeriodType] = useState('all');   // 'all' | 'fy' | 'custom'
  const [fyYear, setFyYear] = useState('');              // e.g. '2025' → FY Apr 2024 – Mar 2025
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');

  // Build date range from current period selection (returns { dateFrom, dateTo } or {})
  const getPeriodParams = () => {
    if (periodType === 'fy' && fyYear) {
      const startYear = parseInt(fyYear) - 1;
      return { dateFrom: `${startYear}-04-01`, dateTo: `${fyYear}-03-31` };
    }
    if (periodType === 'custom') {
      const params = {};
      if (periodFrom) params.dateFrom = periodFrom;
      if (periodTo) params.dateTo = periodTo;
      return params;
    }
    return {};
  };

  // Generate list of financial years (current + 5 previous)
  const financialYears = (() => {
    const now = new Date();
    const currentFY = now.getMonth() >= 3 ? now.getFullYear() + 1 : now.getFullYear();
    return Array.from({ length: 6 }, (_, i) => currentFY - i);
  })();

  // Column picker
  const [visibleColumns, setVisibleColumns] = useState(loadVendorColPrefs);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const columnPickerRef = useRef(null);
  const col = (key) => visibleColumns.includes(key);
  const toggleColumn = (key) => {
    if (VENDOR_COLUMNS.find(c => c.key === key)?.locked) return;
    setVisibleColumns(prev => {
      const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
      localStorage.setItem(VENDOR_COL_KEY, JSON.stringify(next));
      return next;
    });
  };
  const resetColumns = () => {
    setVisibleColumns(VENDOR_COL_DEFAULTS);
    localStorage.setItem(VENDOR_COL_KEY, JSON.stringify(VENDOR_COL_DEFAULTS));
  };

  useEffect(() => {
    function handler(e) {
      if (columnPickerRef.current && !columnPickerRef.current.contains(e.target)) setShowColumnPicker(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  
  const [formData, setFormData] = useState({
    vendorName: '',
    contactEmail: '',
    contactPhone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    taxId: '',
    paymentTerms: '',
    bankAccount: '',
    notes: ''
  });

  // Check if user can manage vendors
  const canManageVendors = hasAnyPermission(['manage_vendors', 'manage_budgets']);

  useEffect(() => {
    fetchVendors();
    fetchGLAccounts();
    fetchDepartments();
  }, []);

  // Re-fetch when period filter changes (skip initial mount — handled by the main useEffect above)
  const periodMountRef = useRef(false);
  useEffect(() => {
    if (!periodMountRef.current) { periodMountRef.current = true; return; }
    if (periodType === 'fy' && !fyYear) return; // wait until year is selected
    fetchVendors();
  }, [periodType, fyYear, periodFrom, periodTo]);

  const fetchGLAccounts = async () => {
    try {
      const response = await apiClient.get('/gl-accounts');
      const glData = response.data.glAccounts || response.data || [];
      setGlAccounts(Array.isArray(glData) ? glData : []);
    } catch (error) {
      console.error('Failed to fetch GL accounts:', error);
      setGlAccounts([]);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await apiClient.get('/departments');
      const deptData = response.data.departments || response.data || [];
      setDepartments(Array.isArray(deptData) ? deptData : []);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
      setDepartments([]);
    }
  };

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const periodParams = getPeriodParams();
      const response = await apiClient.get('/vendors', {
        params: { 
          page: 1, 
          pageSize: 1000,
          includeAll: true,
          ...periodParams,
        }
      });
      
      const vendorList = response.data.vendors || response.data || [];
      setVendors(vendorList);
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
      alert('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingVendor(null);
    setFormData({
      vendorName: '',
      contactEmail: '',
      contactPhone: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      taxId: '',
      paymentTerms: '',
      bankAccount: '',
      notes: ''
    });
    setShowModal(true);
  };

  const handleEdit = (vendor) => {
    setEditingVendor(vendor);
    setFormData({
      vendorName: vendor.VendorName || '',
      contactEmail: vendor.ContactEmail || '',
      contactPhone: vendor.ContactPhone || '',
      addressLine1: vendor.AddressLine1 || '',
      addressLine2: vendor.AddressLine2 || '',
      city: vendor.City || '',
      state: vendor.State || '',
      postalCode: vendor.PostalCode || '',
      country: vendor.Country || '',
      taxId: vendor.TaxID || '',
      paymentTerms: vendor.PaymentTerms || '',
      bankAccount: vendor.BankAccount || '',
      notes: vendor.Notes || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingVendor) {
        await apiClient.put(`/vendors/${editingVendor.VendorID}`, formData);
      } else {
        await apiClient.post('/vendors', formData);
      }
      
      setShowModal(false);
      fetchVendors();
    } catch (error) {
      console.error('Failed to save vendor:', error);
      alert(error.response?.data?.message || 'Failed to save vendor');
    }
  };

  const handleDelete = async (deleteType, reassignToVendorId) => {
    if (!deleteModalVendor) return;
    try {
      let url = `/vendors/${deleteModalVendor.VendorID}?type=${deleteType}`;
      if (deleteType === 'hard' && reassignToVendorId) {
        url += `&reassignTo=${reassignToVendorId}`;
      }
      await apiClient.delete(url);
      setDeleteModalVendor(null);
      alert(deleteType === 'hard' ? 'Vendor permanently deleted' : 'Vendor archived successfully');
      fetchVendors();
    } catch (error) {
      console.error('Failed to delete vendor:', error);
      alert(error.response?.data?.message || 'Failed to delete vendor');
      throw error;
    }
  };
  const toggleVendorSelection = (vendorId) => {
    setSelectedVendors(prev => 
      prev.includes(vendorId) 
        ? prev.filter(id => id !== vendorId)
        : [...prev, vendorId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedVendors.length === filteredVendors.length) {
      setSelectedVendors([]);
    } else {
      setSelectedVendors(filteredVendors.map(v => v.VendorID));
    }
  };

  const handleBulkApprove = async () => {
    if (selectedVendors.length === 0) {
      alert('Please select at least one vendor to approve');
      return;
    }

    if (!confirm(`Are you sure you want to approve ${selectedVendors.length} vendor(s)?`)) {
      return;
    }

    try {
      await apiClient.post('/vendors/bulk-approve', {
        vendorIds: selectedVendors
      });
      alert(`Successfully approved ${selectedVendors.length} vendor(s)`);
      setSelectedVendors([]);
      fetchVendors();
    } catch (error) {
      console.error('Failed to approve vendors:', error);
      alert(error.response?.data?.message || 'Failed to approve vendors');
    }
  };
  const fetchDuplicates = async () => {
    try {
      const response = await apiClient.get('/vendors/duplicates');
      setDuplicates(response.data.duplicateGroups || []);
      setShowMergeModal(true);
    } catch (error) {
      console.error('Failed to fetch duplicates:', error);
      alert('Failed to load duplicate vendors');
    }
  };

  const handleMergeVendors = async () => {
    if (!selectedKeepVendor || !selectedRemoveVendor) {
      alert('Please select both vendors to merge');
      return;
    }

    if (selectedKeepVendor === selectedRemoveVendor) {
      alert('Cannot merge a vendor with itself');
      return;
    }

    const keepVendor = vendors.find(v => v.VendorID === selectedKeepVendor);
    const removeVendor = vendors.find(v => v.VendorID === selectedRemoveVendor);

    if (!confirm(
      `Merge "${removeVendor.VendorName}" into "${keepVendor.VendorName}"?\n\n` +
      `This will move ${removeVendor.InvoiceCount || 0} invoice(s) and deactivate "${removeVendor.VendorName}".`
    )) return;

    try {
      const response = await apiClient.post(`/vendors/${selectedKeepVendor}/merge/${selectedRemoveVendor}`);
      alert(`Success! Merged vendors. Moved ${response.data.invoicesMoved} invoice(s).`);
      setShowMergeModal(false);
      setSelectedKeepVendor(null);
      setSelectedRemoveVendor(null);
      fetchVendors();
    } catch (error) {
      console.error('Failed to merge vendors:', error);
      alert(error.response?.data?.message || 'Failed to merge vendors');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      const vendors = jsonData.map(row => ({
        vendorName: String(row['Vendor Name'] || row['VendorName'] || '').trim(),
        contactEmail: String(row['Contact Email'] || row['ContactEmail'] || '').trim(),
        contactPhone: String(row['Contact Phone'] || row['ContactPhone'] || '').trim(),
        addressLine1: String(row['Address Line 1'] || row['AddressLine1'] || '').trim(),
        addressLine2: String(row['Address Line 2'] || row['AddressLine2'] || '').trim(),
        city: String(row['City'] || '').trim(),
        state: String(row['State'] || '').trim(),
        postalCode: String(row['Postal Code'] || row['PostalCode'] || '').trim(),
        country: String(row['Country'] || '').trim(),
        taxId: String(row['Tax ID'] || row['TaxID'] || '').trim(),
        paymentTerms: String(row['Payment Terms'] || row['PaymentTerms'] || '').trim(),
        bankAccount: String(row['Bank Account'] || row['BankAccount'] || '').trim(),
        notes: String(row['Notes'] || '').trim()
      })).filter(v => v.vendorName);

      if (vendors.length === 0) {
        alert('No valid vendors found in the file');
        return;
      }

      const response = await apiClient.post('/vendors/bulk', { vendors });
      
      alert(`Import successful!\nAdded: ${response.data.added}\nSkipped: ${response.data.skipped}`);
      fetchVendors();
    } catch (error) {
      console.error('Import error:', error);
      alert('Failed to import vendors');
    }
    
    e.target.value = '';
  };

  const handleExport = () => {
    const exportData = vendors.map(v => ({
      'Vendor Name': v.VendorName,
      'Contact Email': v.ContactEmail || '',
      'Contact Phone': v.ContactPhone || '',
      'Address Line 1': v.AddressLine1 || '',
      'Address Line 2': v.AddressLine2 || '',
      'City': v.City || '',
      'State': v.State || '',
      'Postal Code': v.PostalCode || '',
      'Country': v.Country || '',
      'Tax ID': v.TaxID || '',
      'Payment Terms': v.PaymentTerms || '',
      'Bank Account': v.BankAccount || '',
      'Status': v.IsActive ? 'Active' : 'Inactive',
      'Notes': v.Notes || ''
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Vendors');
    XLSX.writeFile(wb, 'vendors_export.xlsx');
  };

  const filteredVendors = vendors.filter(vendor => {
    const matchesSearch = 
      vendor.VendorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vendor.ContactEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vendor.TaxID?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = 
      statusFilter === 'all' || 
      (statusFilter === 'active' && vendor.IsActive) ||
      (statusFilter === 'inactive' && !vendor.IsActive);

    const matchesApproval =
      !approvalFilter || vendor.ApprovalStatus === approvalFilter;
    
    return matchesSearch && matchesStatus && matchesApproval;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1" /> 
      : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  const sortedVendors = useMemo(() => {
    return [...filteredVendors].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === 'InvoiceCount' || sortField === 'GLCount') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else if (sortField === 'CreatedAt' || sortField === 'ApprovedAt') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      } else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredVendors, sortField, sortDirection]);

  const pagedVendors = useMemo(
    () => sortedVendors.slice((vendorsPage - 1) * VENDORS_PAGE_SIZE, vendorsPage * VENDORS_PAGE_SIZE),
    [sortedVendors, vendorsPage]
  );

  // Reset page when filters/search change
  useEffect(() => { setVendorsPage(1); }, [searchTerm, statusFilter, approvalFilter, glFilter, departmentFilter]);

  const fetchVendorGLs = async (vendorId) => {
    try {
      const response = await apiClient.get(`/vendors/${vendorId}/gl-accounts`);
      setVendorGLs(prev => ({ ...prev, [vendorId]: response.data.glAccounts || response.data }));
    } catch (error) {
      console.error('Failed to fetch vendor GL accounts:', error);
      alert('Failed to load GL accounts for vendor');
    }
  };

  const fetchVendorDepartments = async (vendorId) => {
    try {
      const response = await apiClient.get(`/vendors/${vendorId}/departments`);
      setVendorDepartments(prev => ({ ...prev, [vendorId]: response.data.departments || response.data }));
    } catch (error) {
      console.error('Failed to fetch vendor departments:', error);
      alert('Failed to load departments for vendor');
    }
  };

  const handleAssignDepartments = async (vendor) => {
    setSelectedVendorForDepts(vendor);
    
    // Fetch current assignments
    try {
      const response = await apiClient.get(`/vendors/${vendor.VendorID}/departments`);
      const deptData = response.data.departments || response.data;
      const assignedDeptIds = deptData.map(d => d.DepartmentID);
      setSelectedDepartmentIds(assignedDeptIds);
      setShowDepartmentModal(true);
    } catch (error) {
      console.error('Failed to fetch vendor departments:', error);
      setSelectedDepartmentIds([]);
      setShowDepartmentModal(true);
    }
  };

  const handleSaveDepartmentAssignments = async () => {
    try {
      // Validate at least one department is selected
      if (selectedDepartmentIds.length === 0) {
        alert('Please select at least one department to assign this vendor to.');
        return;
      }

      await apiClient.post(`/vendors/${selectedVendorForDepts.VendorID}/departments`, {
        departmentIds: selectedDepartmentIds
      });
      
      alert('Vendor departments updated successfully');
      setShowDepartmentModal(false);
      
      // Refresh vendor departments
      await fetchVendorDepartments(selectedVendorForDepts.VendorID);
      
    } catch (error) {
      console.error('Failed to assign departments:', error);
      const errorMessage = error.response?.data?.message || 'Failed to assign departments to vendor';
      alert(`Error: ${errorMessage}`);
    }
  };

  const toggleDepartmentSelection = (deptId) => {
    setSelectedDepartmentIds(prev => 
      prev.includes(deptId)
        ? prev.filter(id => id !== deptId)
        : [...prev, deptId]
    );
  };

  const toggleVendorExpansion = async (vendorId) => {
    if (expandedVendor === vendorId) {
      setExpandedVendor(null);
    } else {
      setExpandedVendor(vendorId);
      if (!vendorGLs[vendorId]) {
        await fetchVendorGLs(vendorId);
      }
      if (!vendorDepartments[vendorId]) {
        await fetchVendorDepartments(vendorId);
      }
    }
  };

  const navigateToInvoices = (vendorId, vendorName) => {
    navigate('/invoices', { state: { vendorFilter: vendorId, vendorName } });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Loading vendors...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Vendors</h1>
        <div className="flex gap-3">
          {selectedVendors.length > 0 && (
            <button
              onClick={handleBulkApprove}
              className="btn btn-success"
            >
              <CheckSquare className="w-4 h-4" />
              Approve Selected ({selectedVendors.length})
            </button>
          )}
          <button
            onClick={fetchDuplicates}
            className="btn btn-warning"
          >
            <GitMerge className="w-4 h-4" />
            Merge Duplicates
          </button>
          <label className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer flex items-center gap-2">
            <Upload className="w-4 h-4" />
            Import
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImport}
              className="hidden"
            />
          </label>
          <button
            onClick={handleExport}
            className="btn btn-success"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
          <button
            onClick={handleCreate}
            className="btn btn-primary"
          >
            <Plus className="w-4 h-4" />
            Add Vendor
          </button>
        </div>
      </div>

      {/* Filters + Column Picker */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, or tax ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-9 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-300 focus:border-blue-400"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-300 bg-white">
          <option value="all">All Status</option>
          <option value="active">Active Only</option>
          <option value="inactive">Inactive Only</option>
        </select>
        <select value={approvalFilter} onChange={(e) => setApprovalFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-300 bg-white"
          title="Approval status">
          <option value="">All Approvals</option>
          <option value="Pending">Pending Approval</option>
          <option value="Approved">Approved</option>
          <option value="Rejected">Rejected</option>
        </select>
        <select value={glFilter} onChange={(e) => setGlFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-300 bg-white">
          <option value="">All GL Accounts</option>
          {glAccounts.map(gl => (
            <option key={gl.GLAccountID} value={gl.GLAccountID}>{gl.AccountCode} - {gl.AccountName}</option>
          ))}
        </select>
        <select value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-300 bg-white">
          <option value="">All Departments</option>
          {departments.map(dept => (
            <option key={dept.DepartmentID} value={dept.DepartmentName}>{dept.DepartmentName}</option>
          ))}
        </select>

        {/* Period filter */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 text-sm text-gray-500">
            <Calendar className="w-4 h-4" />
            <span className="font-medium">Invoice Period:</span>
          </div>
          <select
            value={periodType}
            onChange={(e) => { setPeriodType(e.target.value); setFyYear(''); setPeriodFrom(''); setPeriodTo(''); }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-300 bg-white"
          >
            <option value="all">All Time</option>
            <option value="fy">Financial Year</option>
            <option value="custom">Custom Period</option>
          </select>
          {periodType === 'fy' && (
            <select
              value={fyYear}
              onChange={(e) => setFyYear(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-300 bg-white"
            >
              <option value="">Select year…</option>
              {financialYears.map(y => (
                <option key={y} value={y}>FY {y - 1}/{String(y).slice(-2)}</option>
              ))}
            </select>
          )}
          {periodType === 'custom' && (
            <>
              <input
                type="date"
                value={periodFrom}
                onChange={(e) => setPeriodFrom(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-300 bg-white"
                title="From date"
              />
              <span className="text-gray-400 text-sm">–</span>
              <input
                type="date"
                value={periodTo}
                onChange={(e) => setPeriodTo(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-300 bg-white"
                title="To date"
              />
              {(periodFrom || periodTo) && (
                <button
                  onClick={() => { setPeriodFrom(''); setPeriodTo(''); }}
                  className="text-gray-400 hover:text-gray-600"
                  title="Clear dates"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </>
          )}
        </div>

        {/* Column picker */}
        <div className="relative ml-auto" ref={columnPickerRef}>
          <button
            onClick={() => setShowColumnPicker(p => !p)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm border rounded-lg transition-colors ${
              showColumnPicker ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
            title="Show/hide columns"
          >
            <Columns3 className="w-4 h-4" /> Columns
          </button>
          {showColumnPicker && (
            <div className="absolute right-0 top-10 z-30 w-52 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Columns</span>
                <button onClick={resetColumns} className="text-xs text-blue-600 hover:underline">Reset</button>
              </div>
              <div className="py-1">
                {VENDOR_COLUMNS.map(c => (
                  <button key={c.key} onClick={() => toggleColumn(c.key)} disabled={c.locked}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-default transition-colors">
                    <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                      col(c.key) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                    }`}>
                      {col(c.key) && <Check className="w-3 h-3 text-white" />}
                    </span>
                    {c.label}
                    {c.locked && <span className="ml-auto text-[10px] text-gray-400">locked</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <span className="text-sm text-gray-500 whitespace-nowrap">Showing {filteredVendors.length} of {vendors.length}</span>
      </div>

      {/* Vendors Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 w-10">
                <button onClick={toggleSelectAll} className="text-gray-500 hover:text-gray-700">
                  {selectedVendors.length === filteredVendors.length && filteredVendors.length > 0
                    ? <CheckSquare className="w-4 h-4 text-blue-600" />
                    : <Square className="w-4 h-4" />}
                </button>
              </th>
              <th className="px-3 py-3 w-8"></th>
              <th className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer select-none" onClick={() => handleSort('VendorName')}><span className="inline-flex items-center gap-1">Vendor Name<SortIcon field="VendorName" /></span></th>
              {col('contact') && <th className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer select-none" onClick={() => handleSort('ContactEmail')}><span className="inline-flex items-center gap-1">Contact<SortIcon field="ContactEmail" /></span></th>}
              {col('taxId') && <th className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer select-none" onClick={() => handleSort('TaxID')}><span className="inline-flex items-center gap-1">Tax ID<SortIcon field="TaxID" /></span></th>}
              {col('paymentTerms') && <th className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer select-none" onClick={() => handleSort('PaymentTerms')}><span className="inline-flex items-center gap-1">Payment Terms<SortIcon field="PaymentTerms" /></span></th>}
              {col('invoices') && <th className="px-4 py-3 text-center font-medium text-gray-600 cursor-pointer select-none" onClick={() => handleSort('InvoiceCount')}>
                <span className="inline-flex items-center gap-1">
                  {periodType === 'all' ? 'Invoices' : 'Invoices (period)'}
                  <SortIcon field="InvoiceCount" />
                  {periodType !== 'all' && <Calendar className="w-3 h-3 text-blue-500 ml-0.5" title="Filtered by period" />}
                </span>
              </th>}
              {col('gls') && <th className="px-4 py-3 text-center font-medium text-gray-600 cursor-pointer select-none" onClick={() => handleSort('GLCount')}><span className="inline-flex items-center gap-1">GLs<SortIcon field="GLCount" /></span></th>}
              {col('approval') && <th className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer select-none" onClick={() => handleSort('ApprovalStatus')}><span className="inline-flex items-center gap-1">Approval<SortIcon field="ApprovalStatus" /></span></th>}
              {col('created') && <th className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer select-none" onClick={() => handleSort('CreatedAt')}><span className="inline-flex items-center gap-1">Created<SortIcon field="CreatedAt" /></span></th>}
              {col('approved') && <th className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer select-none" onClick={() => handleSort('ApprovedAt')}><span className="inline-flex items-center gap-1">Approved<SortIcon field="ApprovedAt" /></span></th>}
              {col('status') && <th className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer select-none" onClick={() => handleSort('IsActive')}><span className="inline-flex items-center gap-1">Status<SortIcon field="IsActive" /></span></th>}
              <th className="px-4 py-3 text-right font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sortedVendors.length > 0 ? (
              pagedVendors.map((vendor) => (
                <React.Fragment key={vendor.VendorID}>
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <button onClick={() => toggleVendorSelection(vendor.VendorID)} className="text-gray-400 hover:text-gray-700">
                        {selectedVendors.includes(vendor.VendorID)
                          ? <CheckSquare className="w-4 h-4 text-blue-600" />
                          : <Square className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="px-3 py-3">
                      <button onClick={() => toggleVendorExpansion(vendor.VendorID)} className="text-gray-400 hover:text-gray-700">
                        {expandedVendor === vendor.VendorID
                          ? <ChevronDown className="w-4 h-4" />
                          : <ChevronRight className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>{vendor.VendorName}</span>
                        {getVendorRisks(vendor).map((risk, i) => (
                          <span
                            key={i}
                            title={risk.title}
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium cursor-help ${
                              risk.type === 'error' ? 'bg-red-100 text-red-700' :
                              risk.type === 'warning' ? 'bg-amber-100 text-amber-700' :
                              'bg-blue-100 text-blue-700'
                            }`}
                          >
                            <AlertTriangle className="w-3 h-3" />
                            {risk.label}
                          </span>
                        ))}
                      </div>
                    </td>
                    {col('contact') && <td className="px-4 py-3 text-gray-600">
                      <div>{vendor.ContactEmail || '—'}</div>
                      {vendor.ContactPhone && <div className="text-xs text-gray-400">{vendor.ContactPhone}</div>}
                    </td>}
                    {col('taxId') && <td className="px-4 py-3 text-gray-600 font-mono text-xs">{vendor.TaxID || '—'}</td>}
                    {col('paymentTerms') && <td className="px-4 py-3 text-gray-600">{vendor.PaymentTerms || '—'}</td>}
                    {col('invoices') && <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => navigateToInvoices(vendor.VendorID, vendor.VendorName)}
                        disabled={!vendor.InvoiceCount}
                        className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold ${
                          vendor.InvoiceCount > 0 ? 'bg-blue-100 text-blue-700 hover:bg-blue-200 cursor-pointer' : 'bg-gray-100 text-gray-400 cursor-default'
                        }`}
                      >{vendor.InvoiceCount || 0}</button>
                    </td>}
                    {col('gls') && <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => toggleVendorExpansion(vendor.VendorID)}
                        className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold ${
                          vendor.GLCount > 0 ? 'bg-purple-100 text-purple-700 hover:bg-purple-200 cursor-pointer' : 'bg-gray-100 text-gray-400 cursor-default'
                        }`}
                      >{vendor.GLCount || 0}</button>
                    </td>}
                    {col('approval') && <td className="px-4 py-3">
                      {vendor.ApprovalStatus === 'Approved' ? (
                        <button
                          onClick={() => { setSelectedVendorForApproval(vendor); setShowApprovalModal(true); }}
                          title="Click to review approval"
                          className="group inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700 border border-green-200 hover:bg-green-600 hover:text-white hover:border-green-600 transition-all cursor-pointer shadow-sm">
                          <CheckCircle className="w-3.5 h-3.5" />
                          Approved
                          <Pencil className="w-2.5 h-2.5 opacity-40 group-hover:opacity-100" />
                        </button>
                      ) : vendor.ApprovalStatus === 'Rejected' ? (
                        <button
                          onClick={() => { setSelectedVendorForApproval(vendor); setShowApprovalModal(true); }}
                          title="Click to review"
                          className="group inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-700 border border-red-200 hover:bg-red-600 hover:text-white hover:border-red-600 transition-all cursor-pointer shadow-sm">
                          <XCircle className="w-3.5 h-3.5" />
                          Rejected
                          <Pencil className="w-2.5 h-2.5 opacity-40 group-hover:opacity-100" />
                        </button>
                      ) : (
                        <button
                          onClick={() => { setSelectedVendorForApproval(vendor); setShowApprovalModal(true); }}
                          title="Click to approve or reject"
                          className="group inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-700 border border-amber-300 hover:bg-amber-500 hover:text-white hover:border-amber-500 transition-all cursor-pointer shadow-sm animate-pulse hover:animate-none">
                          <Clock className="w-3.5 h-3.5" />
                          Pending
                          <Pencil className="w-2.5 h-2.5 opacity-60 group-hover:opacity-100" />
                        </button>
                      )}
                    </td>}
                    {col('created') && <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {vendor.CreatedAt ? (
                        <><div>{new Date(vendor.CreatedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                        <div className="text-xs text-gray-400">{new Date(vendor.CreatedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</div></>
                      ) : '—'}
                    </td>}
                    {col('approved') && <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {vendor.ApprovedAt ? (
                        <><div>{new Date(vendor.ApprovedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                        <div className="text-xs text-gray-400">{new Date(vendor.ApprovedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}{vendor.ApprovedByName && ` · ${vendor.ApprovedByName}`}</div></>
                      ) : <span className="text-gray-400">—</span>}
                    </td>}
                    {col('status') && <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${
                        vendor.IsActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}>{vendor.IsActive ? 'Active' : 'Inactive'}</span>
                    </td>}
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => handleEdit(vendor)}
                          title="Edit vendor"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-white border border-blue-200 rounded-lg hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all shadow-sm">
                          <Edit2 className="w-3 h-3" />Edit
                        </button>
                        {canManageVendors && (
                          <button
                            onClick={() => handleAssignDepartments(vendor)}
                            title="Assign departments"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-violet-700 bg-white border border-violet-200 rounded-lg hover:bg-violet-600 hover:text-white hover:border-violet-600 transition-all shadow-sm">
                            <Building2 className="w-3 h-3" />Depts
                          </button>
                        )}
                        <button
                          onClick={() => setDeleteModalVendor(vendor)}
                          title="Delete vendor"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-600 hover:text-white hover:border-red-600 transition-all shadow-sm">
                          <Trash2 className="w-3 h-3" />Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedVendor === vendor.VendorID && (
                    <tr className="bg-purple-50">
                      <td colSpan={3 + VENDOR_COLUMNS.filter(c => col(c.key) && c.key !== 'vendorName').length + 1} className="px-6 py-4">
                        <div className="bg-white rounded-lg p-4 shadow-inner">
                          <h4 className="font-semibold text-gray-900 mb-3">GL Accounts for {vendor.VendorName}</h4>
                          {vendorGLs[vendor.VendorID] ? (
                            vendorGLs[vendor.VendorID].length > 0 ? (
                              <table className="min-w-full text-sm">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Account #</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Account Name</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Type</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Department(s)</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Lines</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Total Amount</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                  {vendorGLs[vendor.VendorID].map(gl => (
                                    <tr key={gl.GLAccountID} className="hover:bg-gray-50">
                                      <td className="px-4 py-2 font-mono">{gl.AccountCode}</td>
                                      <td className="px-4 py-2">{gl.AccountName}</td>
                                      <td className="px-4 py-2">
                                        <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800">
                                          {gl.AccountType}
                                        </span>
                                      </td>
                                      <td className="px-4 py-2 text-gray-600">
                                        {gl.Departments
                                          ? [...new Set(gl.Departments.split(',').map(s => s.trim()).filter(Boolean))].join(', ')
                                          : 'N/A'}
                                      </td>
                                      <td className="px-4 py-2 text-right">{gl.LineCount}</td>
                                      <td className="px-4 py-2 text-right font-semibold">
                                        {gl.TotalAmount
                                          ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: gl.Currency || 'GBP' }).format(parseFloat(gl.TotalAmount))
                                          : '£0.00'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <p className="text-gray-500 text-center py-4">No GL accounts found for this vendor</p>
                            )
                          ) : (
                            <p className="text-gray-500 text-center py-4">Loading GL accounts...</p>
                          )}
                        </div>

                        {/* Assigned Departments Section */}
                        <div className="bg-white rounded-lg p-4 shadow-inner mt-4">
                          <h4 className="font-semibold text-gray-900 mb-3">Assigned Departments</h4>
                          {vendorDepartments[vendor.VendorID] ? (
                            vendorDepartments[vendor.VendorID].length > 0 ? (
                              <div className="flex flex-wrap gap-2">
                                {vendorDepartments[vendor.VendorID].map((dept) => (
                                  <span
                                    key={dept.DepartmentID}
                                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800"
                                  >
                                    {dept.DepartmentName}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <p className="text-gray-500 text-center py-4">
                                No departments assigned. {canManageVendors && 'Click "Assign Depts" to assign this vendor to departments.'}
                              </p>
                            )
                          ) : (
                            <p className="text-gray-500 text-center py-4">Loading departments...</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan={3 + VENDOR_COLUMNS.filter(c => col(c.key) && c.key !== 'vendorName').length + 1} className="px-6 py-10 text-center text-gray-400">
                  No vendors found. Click "Add Vendor" or "Import" to add vendors.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <TablePagination page={vendorsPage} pageSize={VENDORS_PAGE_SIZE} total={sortedVendors.length} onPageChange={setVendorsPage} />
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {editingVendor ? 'Edit Vendor' : 'Create Vendor'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vendor Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.vendorName}
                    onChange={(e) => setFormData({ ...formData, vendorName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.contactPhone}
                    onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address Line 1
                  </label>
                  <input
                    type="text"
                    value={formData.addressLine1}
                    onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Street address"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address Line 2
                  </label>
                  <input
                    type="text"
                    value={formData.addressLine2}
                    onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Suite, floor, building (optional)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State / Region
                  </label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Postal Code
                  </label>
                  <input
                    type="text"
                    value={formData.postalCode}
                    onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country
                  </label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tax ID / VAT Number
                  </label>
                  <input
                    type="text"
                    value={formData.taxId}
                    onChange={(e) => setFormData({ ...formData, taxId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Terms
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Net 30"
                    value={formData.paymentTerms}
                    onChange={(e) => setFormData({ ...formData, paymentTerms: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bank Account
                  </label>
                  <input
                    type="text"
                    value={formData.bankAccount}
                    onChange={(e) => setFormData({ ...formData, bankAccount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="btn btn-primary flex-1"
                >
                  {editingVendor ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Merge Duplicates Modal */}
      {showMergeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
                Merge Duplicate Vendors
              </h2>
              <button
                onClick={() => {
                  setShowMergeModal(false);
                  setSelectedKeepVendor(null);
                  setSelectedRemoveVendor(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {duplicates.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-green-600 font-medium">✓ No duplicate vendors found!</p>
                <p className="text-gray-500 text-sm mt-2">All vendor names are unique.</p>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <p className="text-sm text-orange-800">
                    <strong>Found {duplicates.length} duplicate group(s).</strong> Select which vendor to keep and which to merge.
                  </p>
                </div>

                {duplicates.map((group, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <h3 className="font-semibold text-gray-900 mb-3">
                      Group {idx + 1}: "{group.normalizedName}" ({group.count} vendors)
                    </h3>
                    <div className="space-y-2">
                      {group.vendors.map(vendor => (
                        <div 
                          key={vendor.vendorId}
                          className={`bg-white border rounded-lg p-3 flex items-center justify-between ${
                            selectedKeepVendor === vendor.vendorId ? 'border-green-500 bg-green-50' :
                            selectedRemoveVendor === vendor.vendorId ? 'border-red-500 bg-red-50' :
                            'border-gray-200'
                          }`}
                        >
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{vendor.vendorName}</div>
                            <div className="text-sm text-gray-600">
                              {vendor.contactEmail && <span>📧 {vendor.contactEmail}</span>}
                              {vendor.taxId && <span className="ml-3">🏛️ {vendor.taxId}</span>}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {vendor.invoiceCount} invoice(s) • {vendor.isActive ? 'Active' : 'Inactive'}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setSelectedKeepVendor(vendor.vendorId);
                                if (selectedRemoveVendor === vendor.vendorId) {
                                  setSelectedRemoveVendor(null);
                                }
                              }}
                              className={`btn btn-sm ${selectedKeepVendor === vendor.vendorId ? 'btn-success' : 'btn-secondary'}`}
                            >
                              ✓ Keep
                            </button>
                            <button
                              onClick={() => {
                                setSelectedRemoveVendor(vendor.vendorId);
                                if (selectedKeepVendor === vendor.vendorId) {
                                  setSelectedKeepVendor(null);
                                }
                              }}
                              className={`btn btn-sm ${selectedRemoveVendor === vendor.vendorId ? 'btn-danger' : 'btn-secondary'}`}
                              disabled={vendor.invoiceCount === 0}
                            >
                              ✗ Merge
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleMergeVendors}
                    disabled={!selectedKeepVendor || !selectedRemoveVendor}
                    className="btn btn-warning flex-1"
                  >
                    Merge Selected Vendors
                  </button>
                  <button
                    onClick={() => {
                      setShowMergeModal(false);
                      setSelectedKeepVendor(null);
                      setSelectedRemoveVendor(null);
                    }}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Vendor Approval Modal */}
      {showApprovalModal && selectedVendorForApproval && (
        <VendorApprovalModal
          vendorId={selectedVendorForApproval.VendorID}
          vendorName={selectedVendorForApproval.VendorName}
          onClose={() => {
            setShowApprovalModal(false);
            setSelectedVendorForApproval(null);
          }}
          onApproved={() => {
            fetchVendors(); // Refresh vendors list
          }}
        />
      )}

      {/* Delete Vendor Modal */}
      {deleteModalVendor && (
        <DeleteVendorModal
          vendor={deleteModalVendor}
          onConfirm={handleDelete}
          onClose={() => setDeleteModalVendor(null)}
        />
      )}

      {/* Department Assignment Modal */}
      {showDepartmentModal && selectedVendorForDepts && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Assign Departments to {selectedVendorForDepts.VendorName}
            </h2>
            
            <div className="mb-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-gray-600">
                  Select departments that can use this vendor:
                </p>
                <span className="text-sm font-semibold text-purple-600">
                  {selectedDepartmentIds.length} selected
                </span>
              </div>
              
              {departments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No departments found. Please contact your administrator.</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {departments.map((dept) => (
                    <label
                      key={dept.DepartmentID}
                      className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={selectedDepartmentIds.includes(dept.DepartmentID)}
                        onChange={() => toggleDepartmentSelection(dept.DepartmentID)}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <span className="ml-3 text-sm font-medium text-gray-900">
                        {dept.DepartmentName || dept.Name}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSaveDepartmentAssignments}
                className={`btn flex-1 ${
                  selectedDepartmentIds.length > 0
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Save {selectedDepartmentIds.length > 0 ? `(${selectedDepartmentIds.length})` : 'Assignments'}
              </button>
              <button
                onClick={() => {
                  setShowDepartmentModal(false);
                  setSelectedVendorForDepts(null);
                  setSelectedDepartmentIds([]);
                }}
                className="btn btn-secondary flex-1"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
